import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, Panel } from '../ui/Page';
import {
  createShare,
  deleteShare,
  listShares,
  revokeShare,
  type CreateShareInput,
  type ShareView,
} from './api';

/**
 * Handing a file or a folder to somebody with no account (context.md §60–§65). Phase 138.
 *
 * Every field here is a way of taking the link back afterwards, which is the point: an expiry, a
 * number of opens, a number of downloads, and a password. All optional, because "send this to my
 * accountant today" and "here is the catalogue, help yourself" are both real, and a form that
 * forces a ceiling on the second one only teaches people to set it to 9999.
 *
 * **The link is shown once.** The server keeps the token's hash, so it cannot produce the URL
 * again — that is what makes a copy of the database not a copy of every live link, and it is worth
 * saying on screen rather than discovering later.
 */

export function SharePanel({
  subject,
  onClose,
}: {
  subject: { kind: 'FILE' | 'FOLDER'; id: string; name: string };
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [expiresAt, setExpiresAt] = useState('');
  const [maxViews, setMaxViews] = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  const [created, setCreated] = useState<ShareView | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shares = useQuery({ queryKey: ['file-shares'], queryFn: listShares });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['file-shares'] });
  };

  const make = useMutation({
    mutationFn: () => {
      const input: CreateShareInput = {
        ...(subject.kind === 'FILE' ? { fileId: subject.id } : { folderId: subject.id }),
        // An empty box is "no ceiling", not zero: the API refuses zero, and rightly.
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        ...(maxViews ? { maxViews: Number(maxViews) } : {}),
        ...(maxDownloads ? { maxDownloads: Number(maxDownloads) } : {}),
        ...(password ? { password } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      };

      return createShare(input);
    },
    onSuccess: (share) => {
      setError(null);
      setCreated(share);
      setCopied(false);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const revoke = useMutation({
    mutationFn: revokeShare,
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: deleteShare,
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const mine = (shares.data?.shares ?? []).filter((share) =>
    subject.kind === 'FILE' ? share.fileId === subject.id : share.folderId === subject.id,
  );

  return (
    <aside className="drawer" role="dialog" aria-label={t('files.share')}>
      <div className="drawer__head">
        <span className="cell-stack">
          <strong>{t(subject.kind === 'FILE' ? 'files.shareFile' : 'files.shareFolder')}</strong>
          <span className="muted small">{subject.name}</span>
        </span>

        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      <div className="drawer__body stack">
        {created && (
          <div className="share-made">
            <p className="strong">{t('files.shareCreated')}</p>

            <code className="share-made__url">{created.url}</code>

            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => {
                void navigator.clipboard?.writeText(created.url);
                setCopied(true);
              }}
            >
              {copied ? t('files.shareCopied') : t('files.shareCopy')}
            </button>
          </div>
        )}

        <label className="stack__field">
          <span>{t('files.shareExpiry')}</span>
          <input
            className="input"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </label>

        <div className="share-limits">
          <label className="stack__field">
            <span>{t('files.shareMaxViews')}</span>
            <input
              className="input"
              type="number"
              min={1}
              value={maxViews}
              placeholder={t('files.shareNoLimit')}
              onChange={(event) => setMaxViews(event.target.value)}
            />
          </label>

          <label className="stack__field">
            <span>{t('files.shareMaxDownloads')}</span>
            <input
              className="input"
              type="number"
              min={1}
              value={maxDownloads}
              placeholder={t('files.shareNoLimit')}
              onChange={(event) => setMaxDownloads(event.target.value)}
            />
          </label>
        </div>

        <label className="stack__field">
          <span>{t('files.sharePassword')}</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('files.shareNote')}</span>
          <input
            className="input"
            maxLength={200}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="button button--primary"
          disabled={make.isPending}
          onClick={() => make.mutate()}
        >
          {make.isPending ? t('common.saving') : t('files.shareCreate')}
        </button>

        <Panel title={t('files.shares')}>
          {mine.length === 0 ? (
            <p className="muted small">{t('files.shareNone')}</p>
          ) : (
            <ul className="share-list">
              {mine.map((share) => (
                <li key={share.id}>
                  <span className="cell-stack">
                    <span className="cell-inline">
                      <Badge tone={share.revokedAt ? 'neutral' : 'positive'}>
                        {share.revokedAt ? t('files.shareRevoked') : t('files.shareOpen')}
                      </Badge>
                      {share.hasPassword && <Badge tone="info">{t('files.shareProtected')}</Badge>}
                    </span>

                    <span className="muted small">
                      {t('files.shareUsage')
                        .replace('{views}', String(share.views))
                        .replace('{downloads}', String(share.downloads))}
                      {share.expiresAt && ` · ${formatDateTime(share.expiresAt)}`}
                    </span>

                    {share.note && <span className="small">{share.note}</span>}
                  </span>

                  {share.revokedAt ? (
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      onClick={() => remove.mutate(share.id)}
                    >
                      {t('files.shareForget')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      onClick={() => revoke.mutate(share.id)}
                    >
                      {t('files.shareRevoke')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </aside>
  );
}
